import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';

/* ─────────────────────────────────────────────────────────────
   The demo's charts, drawn at the width they actually have.

   They measure their container and draw in real pixels rather than
   stretching one viewBox to fit. Stretching is what squeezes axis labels
   sideways, and a dashboard is mostly axis labels.

   Same marks everywhere: 2px lines, thin bars rounded at the data end only,
   hairline solid gridlines, and a hover readout listing every series. Hover
   only ever adds convenience: every value here is also in a table or a
   figure beside the chart.
   ───────────────────────────────────────────────────────────── */

const AXIS_W = 46;
const AXIS_H = 26;
const TOP = 12;

/* Before layout has run this falls back to a sensible width, which is also
   what the prerender and the tests get. */
const useWidth = (initial = 640) => {
    const ref = useRef(null);
    const [width, setWidth] = useState(initial);

    useLayoutEffect(() => {
        const node = ref.current;
        if (!node) return undefined;
        if (node.clientWidth > 0) setWidth(node.clientWidth);

        const observer = new ResizeObserver(entries => {
            const next = entries[0] && entries[0].contentRect.width;
            if (next > 0) setWidth(next);
        });

        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    return [ref, width];
};

const niceStep = (span, count) => {
    const rough = span / Math.max(1, count);
    const power = 10 ** Math.floor(Math.log10(rough));
    const unit = rough / power;
    const step = unit <= 1 ? 1 : unit <= 2 ? 2 : unit <= 2.5 ? 2.5 : unit <= 5 ? 5 : 10;

    return step * power;
};

export const niceTicks = (min, max, count = 4) => {
    const span = max - min || Math.abs(max) || 1;
    const step = niceStep(span, count);
    const start = Math.floor(min / step) * step;
    const ticks = [];

    for (let value = start; value <= max + step * 0.5; value += step)
        ticks.push(Number(value.toFixed(6)));

    return ticks;
};

function Tip({ x, y, width, title, rows }) {
    const flip = x > width - 180;

    return (
        <div
            className="dash-tip"
            style={{ left: flip ? undefined : x + 12, right: flip ? width - x + 12 : undefined, top: Math.max(0, y) }}
            role="status"
        >
            <span className="dash-tip__title">{title}</span>
            {rows.map(row => (
                <span className="dash-tip__row" key={row.label}>
                    <i className="dash-tip__key" style={{ background: row.color }} aria-hidden="true" />
                    <strong>{row.value}</strong>
                    <span>{row.label}</span>
                </span>
            ))}
        </div>
    );
}

/** Legend keys mirror the mark: a short line for a line, a block for a bar. */
export function Legend({ items, mark = 'line' }) {
    return (
        <ul className="dash-legend">
            {items.map(item => (
                <li key={item.label}>
                    <i className={`dash-legend__key dash-legend__key--${mark}`} style={{ background: item.color }} aria-hidden="true" />
                    {item.label}
                </li>
            ))}
        </ul>
    );
}

const pathOf = (points, x, y) => points
    .map((point, i) => `${i === 0 ? 'M' : 'L'}${x(point[0]).toFixed(1)} ${y(point[1]).toFixed(1)}`)
    .join('');

/**
 * Lines over a shared x. `series` is [{ id, label, color, points: [[x, y]…],
 * area }], with every series on the same x positions so the crosshair can
 * read them all at once. One axis, always.
 */
export function LineChart({
    series,
    height = 260,
    domain,
    yTicks: yTickCount = 4,
    xTicks = [],
    formatX = value => value,
    formatY = value => value,
    formatTip,
    tipTitle,
    xLabel,
    yLabel,
    shade,
    label
}) {
    const [ref, width] = useWidth();
    const [hover, setHover] = useState(null);

    const values = series.flatMap(s => s.points.map(p => p[1]));
    const low = domain ? domain[0] : Math.min(...values);
    const high = domain ? domain[1] : Math.max(...values);
    const pad = domain ? 0 : (high - low || 1) * 0.12;
    /* A quantity that cannot go negative never gets an axis below zero. */
    const ticks = niceTicks(low >= 0 ? Math.max(0, low - pad) : low - pad, high + pad, yTickCount);
    const yMin = domain ? domain[0] : ticks[0];
    const yMax = domain ? domain[1] : ticks[ticks.length - 1];
    const shownTicks = domain ? niceTicks(yMin, yMax, yTickCount).filter(t => t >= yMin && t <= yMax) : ticks;

    const reference = series[0].points;
    const xMin = reference[0][0];
    const xMax = reference[reference.length - 1][0];
    /* A y-axis title gets its own line above the plot, so it can never land on
       the top tick or on a legend sitting above the chart. */
    const top = yLabel ? 26 : TOP;
    const plotW = Math.max(40, width - AXIS_W - 8);
    const plotH = height - AXIS_H - top;

    const x = value => AXIS_W + ((value - xMin) / (xMax - xMin || 1)) * plotW;
    const y = value => top + (1 - (value - yMin) / (yMax - yMin || 1)) * plotH;

    const onMove = event => {
        const rect = event.currentTarget.getBoundingClientRect();
        const target = xMin + ((event.clientX - rect.left - AXIS_W) / plotW) * (xMax - xMin);
        let nearest = 0;
        let best = Infinity;

        /* Evenly spaced in every chart here, so this could index directly,
           but the fast-capture points are not. */
        reference.forEach((point, i) => {
            const gap = Math.abs(point[0] - target);
            if (gap < best) {
                best = gap;
                nearest = i;
            }
        });

        setHover(nearest);
    };

    const hoverX = hover === null ? null : reference[hover][0];

    return (
        <div className="dash-chart" ref={ref} style={{ height }}>
            <svg
                width={width}
                height={height}
                role="img"
                aria-label={label}
                onPointerMove={onMove}
                onPointerLeave={() => setHover(null)}
            >
                {shade && (
                    <rect
                        className="dash-chart__shade"
                        x={x(shade[0])}
                        y={top}
                        width={x(shade[1]) - x(shade[0])}
                        height={plotH}
                    />
                )}

                {shownTicks.map(tick => (
                    <g key={tick}>
                        <line className="dash-chart__grid" x1={AXIS_W} x2={AXIS_W + plotW} y1={y(tick)} y2={y(tick)} />
                        <text className="dash-chart__tick" x={AXIS_W - 8} y={y(tick) + 4} textAnchor="end">{formatY(tick)}</text>
                    </g>
                ))}

                {xTicks.map(tick => (
                    <text
                        className="dash-chart__tick"
                        key={tick}
                        x={Math.min(Math.max(x(tick), AXIS_W + 12), AXIS_W + plotW - 14)}
                        y={height - 8}
                        textAnchor="middle"
                    >
                        {formatX(tick)}
                    </text>
                ))}

                {series.map(s => s.area && (
                    <path
                        key={`${s.id}-area`}
                        d={`${pathOf(s.points, x, y)}L${x(s.points[s.points.length - 1][0]).toFixed(1)} ${top + plotH}L${x(s.points[0][0]).toFixed(1)} ${top + plotH}Z`}
                        fill={s.color}
                        fillOpacity="0.1"
                    />
                ))}

                {series.map(s => (
                    <path key={s.id} d={pathOf(s.points, x, y)} fill="none" stroke={s.color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                ))}

                {hover !== null && (
                    <>
                        <line className="dash-chart__cross" x1={x(hoverX)} x2={x(hoverX)} y1={top} y2={top + plotH} />
                        {series.map(s => (
                            <circle key={s.id} cx={x(hoverX)} cy={y(s.points[hover][1])} r="4" fill={s.color} stroke="var(--bg-panel)" strokeWidth="2" />
                        ))}
                    </>
                )}
            </svg>

            {yLabel && <span className="dash-chart__ylabel">{yLabel}</span>}
            {xLabel && <span className="dash-chart__xlabel">{xLabel}</span>}

            {hover !== null && (
                <Tip
                    x={x(hoverX)}
                    y={top}
                    width={width}
                    title={tipTitle ? tipTitle(hoverX) : formatX(hoverX)}
                    rows={series.map(s => ({
                        label: s.label,
                        color: s.color,
                        value: (formatTip || formatY)(s.points[hover][1])
                    }))}
                />
            )}
        </div>
    );
}

/**
 * Grouped columns. Each group is one hit target, far wider than its thin
 * bars, and answers with a readout of every series in it.
 */
export function GroupedBars({
    groups,
    height = 260,
    tickEvery = 5,
    formatY = value => value,
    formatTip,
    tipTitle,
    tickLabel = group => group.label,
    selected,
    onSelect,
    label,
    showTick = (group, i) => i % tickEvery === 0
}) {
    const [ref, width] = useWidth();
    const [hover, setHover] = useState(null);

    const top = Math.max(0.0001, ...groups.flatMap(g => g.values.map(v => v.value)));
    const ticks = niceTicks(0, top, 4);
    const yMax = ticks[ticks.length - 1];
    const plotW = Math.max(40, width - AXIS_W - 8);
    const plotH = height - AXIS_H - TOP;
    const slot = plotW / groups.length;
    const y = value => TOP + (1 - value / yMax) * plotH;

    const seriesCount = Math.max(...groups.map(g => g.values.length));
    const gap = seriesCount > 1 ? 1 : 0;
    const barW = Math.min(24, Math.max(1.5, (slot * 0.78 - gap * (seriesCount - 1)) / seriesCount));
    const radius = Math.min(4, barW / 2);

    const barPath = (bx, value) => {
        const ty = y(value);
        const base = TOP + plotH;
        if (base - ty < radius) return `M${bx} ${base}V${ty}H${bx + barW}V${base}Z`;
        return `M${bx} ${base}V${ty + radius}Q${bx} ${ty} ${bx + radius} ${ty}H${bx + barW - radius}Q${bx + barW} ${ty} ${bx + barW} ${ty + radius}V${base}Z`;
    };

    return (
        <div className="dash-chart" ref={ref} style={{ height }}>
            <svg width={width} height={height} role="img" aria-label={label} onPointerLeave={() => setHover(null)}>
                {ticks.map(tick => (
                    <g key={tick}>
                        <line className="dash-chart__grid" x1={AXIS_W} x2={AXIS_W + plotW} y1={y(tick)} y2={y(tick)} />
                        <text className="dash-chart__tick" x={AXIS_W - 8} y={y(tick) + 4} textAnchor="end">{formatY(tick)}</text>
                    </g>
                ))}

                {groups.map((group, i) => {
                    const left = AXIS_W + i * slot;
                    const total = barW * group.values.length + gap * (group.values.length - 1);
                    const start = left + (slot - total) / 2;
                    const active = selected === group.key;
                    const lit = active || hover === i;

                    return (
                        <g key={group.key}>
                            {lit && <rect className="dash-chart__band" x={left} y={TOP} width={slot} height={plotH} />}

                            {group.values.map((value, v) => (
                                <path key={value.id} d={barPath(start + v * (barW + gap), value.value)} fill={value.color} />
                            ))}

                            {showTick(group, i) && (
                                <text className="dash-chart__tick" x={left + slot / 2} y={height - 8} textAnchor="middle">
                                    {tickLabel(group)}
                                </text>
                            )}

                            <rect
                                className="dash-chart__hit"
                                x={left}
                                y={TOP}
                                width={slot}
                                height={plotH + AXIS_H}
                                onPointerEnter={() => setHover(i)}
                                onClick={onSelect ? () => onSelect(group.key) : undefined}
                                style={onSelect ? { cursor: 'pointer' } : undefined}
                            />
                        </g>
                    );
                })}
            </svg>

            {hover !== null && (
                <Tip
                    x={AXIS_W + hover * slot + slot / 2}
                    y={TOP}
                    width={width}
                    title={tipTitle ? tipTitle(groups[hover]) : groups[hover].label}
                    rows={groups[hover].values.map(value => ({
                        label: value.label,
                        color: value.color,
                        value: (formatTip || formatY)(value.value)
                    }))}
                />
            )}
        </div>
    );
}

/** Lengths are normalized, so this shows angle and balance, not magnitude. */
export function Phasors({ phases, kind, colors }) {
    const size = 300;
    const center = size / 2;
    const reach = 88;
    const DEG = Math.PI / 180;

    const magnitudes = phases.map(p => (kind === 'current' ? p.i1 : p.v1));
    const largest = Math.max(...magnitudes) || 1;

    const axes = [[0, '0°'], [90, '90°'], [180, '180°'], [-90, '−90°']];

    return (
        <svg
            className="dash-phasors"
            viewBox={`0 0 ${size} ${size}`}
            role="img"
            aria-label={`${kind === 'current' ? 'Current' : 'Voltage'} phasor diagram`}
        >
            <circle className="dash-chart__grid" cx={center} cy={center} r={reach + 12} fill="none" />
            <circle className="dash-chart__grid" cx={center} cy={center} r={(reach + 12) / 2} fill="none" />
            <line className="dash-chart__grid" x1={center - reach - 12} x2={center + reach + 12} y1={center} y2={center} />
            <line className="dash-chart__grid" x1={center} x2={center} y1={center - reach - 12} y2={center + reach + 12} />

            {axes.map(([angle, text]) => (
                <text
                    className="dash-chart__tick"
                    key={text}
                    x={center + Math.cos(angle * DEG) * (reach + 32)}
                    y={center - Math.sin(angle * DEG) * (reach + 26) + 4}
                    textAnchor="middle"
                >
                    {text}
                </text>
            ))}

            {phases.map((phase, i) => {
                const angle = (kind === 'current' ? phase.iAngle : phase.vAngle) * DEG;
                const length = (magnitudes[i] / largest) * reach;
                const tipX = center + Math.cos(angle) * length;
                const tipY = center - Math.sin(angle) * length;
                const wing = 7;
                const left = angle + Math.PI - 0.4;
                const right = angle + Math.PI + 0.4;

                /* Set off to the side of its arrow, not past the tip, where
                   it would land on the axis label for that direction. */
                const labelX = center + Math.cos(angle + 0.2) * (length * 0.72);
                const labelY = center - Math.sin(angle + 0.2) * (length * 0.72) + 4;

                return (
                    <g key={phase.name}>
                        <line x1={center} y1={center} x2={tipX} y2={tipY} stroke={colors[phase.name]} strokeWidth="2" strokeLinecap="round" />
                        <path
                            d={`M${tipX} ${tipY}L${tipX + Math.cos(left) * wing} ${tipY - Math.sin(left) * wing}L${tipX + Math.cos(right) * wing} ${tipY - Math.sin(right) * wing}Z`}
                            fill={colors[phase.name]}
                        />
                        <text className="dash-phasors__label" x={labelX} y={labelY} textAnchor="middle">{phase.name}</text>
                    </g>
                );
            })}

            <circle cx={center} cy={center} r="3" fill="var(--text-dim)" />
        </svg>
    );
}

/** No axes: this is a shape beside a number, and the number is the reading. */
export function Sparkline({ values, width = 92, height = 28 }) {
    const shape = useMemo(() => {
        const low = Math.min(...values);
        const high = Math.max(...values);
        const span = high - low || 1;

        return values.map((value, i) => [
            1 + (i / (values.length - 1)) * (width - 6),
            3 + (1 - (value - low) / span) * (height - 6)
        ]);
    }, [values, width, height]);

    const end = shape[shape.length - 1];

    return (
        <svg className="dash-spark" width={width} height={height} aria-hidden="true">
            <path d={shape.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join('')} fill="none" stroke="var(--text-faint)" strokeWidth="1.5" strokeLinejoin="round" />
            <circle cx={end[0]} cy={end[1]} r="2.5" fill="var(--accent)" />
        </svg>
    );
}
